import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../register/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Navigate, useLocation } from "react-router-dom";

const LatestUploadsViewer = () => {
  const [latestDate, setLatestDate] = useState(null);
  const [files, setFiles] = useState([]);
  const [cePeFiles, setCePeFiles] = useState([]);
  const [selectedCePeFile, setSelectedCePeFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

  const [selectedCeFile, setSelectedCeFile] = useState(null);
  const [selectedPeFile, setSelectedPeFile] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setIsAuthenticated(false);
      } else {
        setIsAuthenticated(true);
        fetchLatestUploads();
      }
    });

    return () => unsubscribe();
  }, [location]);

  const fetchLatestUploads = async () => {
    try {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);

      const formatDate = (date) => date.toISOString().split("T")[0];
      const todayKey = formatDate(today);
      const yesterdayKey = formatDate(yesterday);

      let filesRef = collection(db, "analysis_data", todayKey, "files");
      let snapshot = await getDocs(filesRef);

      let finalDate = todayKey;
      let fetchedFiles = snapshot.docs.map((doc) => doc.data());

      if (fetchedFiles.length === 0) {
        filesRef = collection(db, "analysis_data", yesterdayKey, "files");
        snapshot = await getDocs(filesRef);
        finalDate = yesterdayKey;
        fetchedFiles = snapshot.docs.map((doc) => doc.data());
      }

      const cepe = [];
      const others = [];

      fetchedFiles.forEach((file) => {
        const name = file.fileName.toLowerCase();
        if (name.includes("ce") || name.includes("pe")) {
          cepe.push(file);
        } else {
          others.push(file);
        }
      });

      setLatestDate(finalDate);
      setFiles(others);
      cepe.sort((a, b) => a.fileName.localeCompare(b.fileName));
      setCePeFiles(cepe);
    } catch (error) {
      console.error("Error fetching latest uploads:", error);
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated === false) {
    return <Navigate to="/login?redirect=analysis-data" />;
  }

  if (loading || isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-center">Loading latest uploads...</p>
      </div>
    );
  }

  return (
    <div className="w-full mt-20 mb-16 py-6  bg-white shadow-xl rounded-2xl border border-gray-200 px-1 mx-auto">
      <h2 className="text-2xl font-bold text-indigo-700 mb-8 text-center">
        Latest Data ({latestDate})
      </h2>

      {/* Dropdowns for CE and PE file selection */}
      {cePeFiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <select
            onChange={(e) => {
              const selected = cePeFiles.find(
                (file) => file.fileName === e.target.value
              );
              setSelectedCeFile(selected);
            }}
            className="w-full p-2 border rounded-md"
            defaultValue=""
          >
            <option value="" disabled>
              -- Select strike price --
            </option>
            {cePeFiles.map((file, idx) => (
              <option key={idx} value={file.fileName}>
                {file.fileName.replace(".html", "").replace(/_/g, " ")}
              </option>
            ))}
          </select>

          <select
            onChange={(e) => {
              const selected = cePeFiles.find(
                (file) => file.fileName === e.target.value
              );
              setSelectedPeFile(selected);
            }}
            className="w-full p-2 border rounded-md"
            defaultValue=""
          >
            <option value="" disabled>
              -- Select strike price --
            </option>
            {cePeFiles.map((file, idx) => (
              <option key={idx} value={file.fileName}>
                {file.fileName.replace(".html", "").replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Graph display area - side-by-side or full width */}
      {(selectedCeFile || selectedPeFile) && (
        <div
          className={`grid ${
            selectedCeFile && selectedPeFile
              ? "md:grid-cols-2 gap-4"
              : "grid-cols-1"
          } mb-10`}
        >
          {selectedCeFile && (
            <div className="border rounded-md overflow-hidden">
              <p className="text-gray-800 font-semibold text-center text-base m-2">
                {selectedCeFile.fileName
                  .replace(".html", "")
                  .replace(/_/g, " ")}
              </p>
              <iframe
                src={selectedCeFile.url}
                title={selectedCeFile.fileName}
                className="w-full h-[80vh] rounded-md"
                allowFullScreen
              ></iframe>
            </div>
          )}

          {selectedPeFile && (
            <div className="border rounded-md overflow-hidden">
              <p className="text-gray-800 font-semibold text-center text-base m-2">
                {selectedPeFile.fileName
                  .replace(".html", "")
                  .replace(/_/g, " ")}
              </p>
              <iframe
                src={selectedPeFile.url}
                title={selectedPeFile.fileName}
                className="w-full h-[80vh] rounded-md"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </div>
      )}

      {files.length === 0 ? (
        <p className="text-center text-gray-600 text-lg"></p>
      ) : (
        <ul className="space-y-6 text-sm">
          {files.map((file, idx) => (
            <li key={idx} className="flex flex-col space-y-1 border rounded-md">
              <p className="text-gray-800 font-semibold text-center text-base m-2">
                {file.fileName.replace(".html", "").replace(/_/g, " ")}
              </p>
              <iframe
                src={file.url}
                title={file.fileName}
                className="w-full h-[80vh] rounded-md"
                allowFullScreen
              ></iframe>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LatestUploadsViewer;
